import {HttpClient} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {
    ActivatedRouterStateSnapshot,
    CurrencyService,
    LanguageService,
    RouterState,
    RoutingService
} from '@spartacus/core';
import {ProductListComponentService, ProductListRouteParams, SearchCriteria, ViewConfig} from '@spartacus/storefront';

import {AimoSearchCriteria} from '../../model/misc.model';
import {GTMItemListId} from '../gtm/aimo-gtm.model';

import {AimoProductSearchService} from './search/aimo-product-search.service';
import {combineLatest, Observable} from "rxjs";
import {debounceTime, distinctUntilChanged, map, tap} from "rxjs/operators";

@Injectable({
    providedIn: 'root',
})
export class AimoProductListComponentService extends ProductListComponentService {
    constructor(
        protected productSearchService: AimoProductSearchService,
        protected routing: RoutingService,
        protected activatedRoute: ActivatedRoute,
        protected currencyService: CurrencyService,
        protected languageService: LanguageService,
        protected router: Router,
        protected http: HttpClient,
        protected viewConfig: ViewConfig,
    ) {
        super(productSearchService, routing, activatedRoute, currencyService, languageService, router, viewConfig);
        this.searchByRouting$ =
            combineLatest([
                this.routing.getRouterState().pipe(
                    distinctUntilChanged((x, y) => {
                        return x.state.url === y.state.url && !y.state.url.includes('reload=true');
                    })
                ),
                ...this.siteContext2,
            ]).pipe(
                debounceTime(0),
                map(([routerState, ..._context]) => (routerState as RouterState).state),
                tap((state: ActivatedRouterStateSnapshot) => {
                    const criteria = this.getCriteriaFromRoute(
                        state.params,
                        state.queryParams
                    );

                    this.search(criteria);
                })
            );
    }

    get siteContext2(): Observable<string>[] {
        return [this.languageService.getActive(), this.currencyService.getActive()];
    }

    protected search(criteria: AimoSearchCriteria): void {
        const currentPage = criteria.currentPage;
        const pageSize = criteria.pageSize;
        const sort = criteria.sortCode;
        const followRedirects = criteria.followRedirects === 'true';
        const gtmParams = criteria.gtmParams;

        this.productSearchService.search(
            criteria.query,
            Object.assign(
                {},
                currentPage && {currentPage},
                pageSize && {pageSize},
                sort && {sort},
                followRedirects && {followRedirects},
                gtmParams && {gtmParams},
            ),
        );
    }

    protected getCriteriaFromRoute(
        routeParams: ProductListRouteParams,
        queryParams: AimoSearchCriteria,
        pageSize?: number,
    ): AimoSearchCriteria {
        return {
            query: this.getAimoQueryFromRouteParams(routeParams, queryParams),
            pageSize: queryParams.pageSize || (pageSize > 0 ? pageSize : this.viewConfig.view.defaultPageSize),
            currentPage: queryParams.currentPage,
            sortCode: queryParams.sortCode,
            followRedirects: queryParams.followRedirects,
            gtmParams: this.activatedRoute.snapshot.queryParams.promotion_id ? {
                gtmPromotionId: this.activatedRoute.snapshot.queryParams.promotion_id,
                gtmPromotionName: this.activatedRoute.snapshot.queryParams.promotion_name,
                gtmItemListId: this.activatedRoute.snapshot.queryParams.item_list_id,
                gtmCreativeSlot: this.activatedRoute.snapshot.queryParams.creative_slot
            } : undefined
        };
    }

    getPageItemsForPageSize(pageSize: number, pageNumber: number): void {
        this.routing
            .getRouterState()
            .subscribe((route) => {
                const routeCriteria = this.getCriteriaFromRoute(
                    <ProductListRouteParams>route.state.params,
                    <SearchCriteria>route.state.queryParams,
                    pageSize,
                );
                const criteria = {
                    ...routeCriteria,
                    currentPage: pageNumber
                };
                this.search(criteria);
            })
            .unsubscribe();
    }

    protected getAimoQueryFromRouteParams(
        {query, categoryCode, brandCode}: ProductListRouteParams,
        queryParams: AimoSearchCriteria,
    ): string | undefined {
        let prefix = queryParams.query || query || ':relevance';
        prefix += ':';
        if (AimoProductListComponentService.getDummyCategoryParam(categoryCode) !== undefined) {
            return prefix + AimoProductListComponentService.getDummyCategoryParam(categoryCode);
        }
        if (categoryCode) {
            prefix += 'allCategories:' + categoryCode;
        }
        if (!query) {
            return prefix;
        }
        return super.getQueryFromRouteParams({query: prefix, categoryCode, brandCode} as ProductListRouteParams);
    }

    public static getDummyCategoryParam(categoryCode: string): string {
        if (
            AimoProductListComponentService.isDummyCategory(categoryCode)
        ) {
            return categoryCode + ':true';
        }
        return undefined;
    }

    public static isDummyCategory(categoryCode: string) {
        return categoryCode === 'favorites' ||
            categoryCode === 'novelty' ||
            categoryCode === 'sponsored' ||
            categoryCode === 'seasonal' ||
            categoryCode === 'mostPurchased' ||
            categoryCode === 'campaign' ||
            categoryCode === 'clickAndCollect';
    }

    public static hasAnyCategoryParam(query: string): boolean {
        return (
            query?.indexOf('favorites') >= 0 ||
            query?.indexOf('novelty') >= 0 ||
            query?.indexOf('sponsored') >= 0 ||
            query?.indexOf('seasonal') >= 0 ||
            query?.indexOf('mostPurchased') >= 0 ||
            query?.indexOf('campaign') >= 0 ||
            query?.indexOf('allCategories') >= 0 ||
            query?.indexOf('clickAndCollect') >= 0
        );
    }

    public static getGTMCategoryParamName(query: string): GTMItemListId {
        if (this.getCategoryParam(query, 'favorites')) {
            return GTMItemListId.favorites;
        }
        if (this.getCategoryParam(query, 'novelty')) {
            return GTMItemListId.frontPageNovelty;
        }
        if (this.getCategoryParam(query, 'sponsored')) {
            return GTMItemListId.frontPageSponsored;
        }
        if (this.getCategoryParam(query, 'seasonal')) {
            return GTMItemListId.frontPageSeasonal;
        }
        if (this.getCategoryParam(query, 'mostPurchased')) {
            return GTMItemListId.frontPageMostPurchased;
        }
        if (this.getCategoryParam(query, 'campaign')) {
            return GTMItemListId.frontPageCampaign;
        }
        if (this.getCategoryParam(query, 'allCategories')) {
            return GTMItemListId.product_category_page;
        }
        if (this.getCategoryParam(query, 'clickAndCollect')) {
            return GTMItemListId.frontPageClickAndCollect;
        }
        return null;
    }

    private static getCategoryParam(query: string, name: string): string {
        if (query.indexOf(name) >= 0) {
            const start = query.indexOf(name) + 1;
            return query.substring(start, query.indexOf(':', start) > 0 ? query.indexOf(':', start) : query.length);
        }
        return null;
    }
}
